import { isCurrentNativeSession } from '#features/api/utils/nativeAuth';
import { addPushToken, removePushToken } from '../model/notifications';

type RegistrationContext = { ownerId: string; epoch: number; credential: string };
type RegistrationOptions = {
  input: Parameters<typeof addPushToken>[0];
  isActive?: () => boolean;
  onRegistered: () => void;
};

let ownerId: string | null = null;
let epoch = 0;
let suspended = false;
let queue: Promise<void> = Promise.resolve();
const pending = new Set<Promise<void>>();

/** Stop admission synchronously, before native media cleanup and before JWT revocation. */
export const beginNativePushSignOut = () => {
  epoch += 1;
  suspended = true;
  ownerId = null;
};

export const prepareNativePushRegistration = (
  nextOwnerId: string,
  credential: string | null
): RegistrationContext | null => {
  if (!credential || !isCurrentNativeSession(credential)) return null;
  if (suspended || ownerId !== nextOwnerId) {
    epoch += 1;
    ownerId = nextOwnerId;
    suspended = false;
  }
  return { ownerId: nextOwnerId, epoch, credential };
};

export const isNativePushRegistrationCurrent = (context: RegistrationContext) =>
  !suspended && context.epoch === epoch && context.ownerId === ownerId;

export const refreshNativePushRegistration = (
  context: RegistrationContext,
  credential: string | null
): RegistrationContext | null =>
  credential && isNativePushRegistrationCurrent(context) && isCurrentNativeSession(credential)
    ? { ...context, credential }
    : null;

/** All native AddToken operations share this queue with owner-scoped compensation. */
export const registerNativePushToken = (
  context: RegistrationContext,
  options: RegistrationOptions
): ReturnType<typeof addPushToken> => {
  const isCurrent = () =>
    isNativePushRegistrationCurrent(context) && (options.isActive?.() ?? true);
  const compensate = () => removePushToken({ token: options.input.token }, context.credential);
  const task = queue.then(async () => {
    if (!isCurrent() || !isCurrentNativeSession(context.credential)) return;
    try {
      const result = await addPushToken(options.input, context.credential);
      if (!isCurrent()) {
        await compensate();
        return;
      }
      if (result?.__typename === 'Operation' && result.status === 'OK') {
        options.onRegistered();
      }
      return result;
    } catch (error) {
      // A failed response can still represent a committed AddToken. Logout/account switching
      // must compensate with the captured JWT, never whichever account is now on the phone.
      if (!isCurrent()) {
        await compensate().catch(() => undefined);
      }
      throw error;
    }
  });
  const settled = task.then(
    () => undefined,
    () => undefined
  );
  queue = settled;
  pending.add(settled);
  void settled.then(() => pending.delete(settled));
  return task;
};

/** Await every AddToken and its compensation while the leaving JWT is still valid. */
export const settleNativePushRegistrations = async (): Promise<void> => {
  if (pending.size > 0) {
    await Promise.all(pending);
    return settleNativePushRegistrations();
  }
};
